/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { AppService } from '@services/app/app.service';
import { UiService } from '@services/ui/ui.service';
import { MitekService } from '@services/mitek/mitek.service';
import { FileType } from '@models/file';
import { DocumentPageFormat } from '@models/checklist';
import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Input
} from '@angular/core';
import {
  Camera,
  CameraResultType,
  CameraSource,
  Photo
} from '@capacitor/camera';
import { MitekDocType } from '@models/mitek';
import { takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-file-button',
  templateUrl: './file-button.component.html',
  styleUrls: ['./file-button.component.scss'],
  standalone: false
})
export class FileButtonComponent implements OnInit, OnDestroy {
  /**
   * modalità di acquisizione
   */
  @Input() mode: 'pdf' | 'picture' = 'picture';

  /**
   * formato documenti di acquisizione
   */
  @Input() format: DocumentPageFormat = 'document';

  /**
   * colore bottone
   */
  @Input() color = 'primary';

  /**
   * riempimento bottone
   */
  @Input() fill = 'outline';

  /**
   * flag per avviare l'apertura del picker subito all'init del componente
   */
  @Input() autoOpening = false;

  /**
   * flag per disabilitare il pulsante
   */
  @Input() disabled = false;

  /**
   * evento emesso alla selezione di un file
   */
  @Output() fileChange: EventEmitter<{ src: string; file: File }> =
    new EventEmitter<{ src: string; file: File }>();

  /**
   * form nascosto per gestire il file picker
   */
  private fileForm: HTMLFormElement;

  /**
   * input nascosto per gestire il file picker
   */
  private fileInput: HTMLInputElement;

  /**
   * lista di tipi file accettati divisi per modalità
   */
  private readonly allowedTypes: { [mode: string]: FileType[] } = {
    ['pdf']: [FileType.applicationPdf],
    ['picture']: [FileType.imagePng, FileType.imageJpeg]
  };

  constructor(
    protected appService: AppService,
    protected uiService: UiService,
    protected mitekService: MitekService
  ) {}

  ngOnInit() {
    if (this.autoOpening) {
      this.openPicker();
    }
  }

  ngOnDestroy() {
    this.removeFileElements();
  }

  /**
   * gestisce il click del bottone
   */
  onClick(event: Event): void {
    event.preventDefault();
    this.openPicker();
  }

  /**
   * apre il file picker inbase alla modalità impostata
   */
  openPicker(): void {
    if (this.appService.getConfig().upload.acquisitionMode === 'mitek') {
      this.openMitekPicker();
    } else if (this.mode === 'pdf') {
      this.openPdfPicker();
    } else if (this.mode === 'picture') {
      this.openPicturePicker();
    }
  }

  /**
   * apre il file picker
   */
  private openPdfPicker(): void {
    this.createFileElements();
    this.fileInput.click();
  }

  /**
   * funzione per gestire l'evento
   */
  private fileInputChangeListener = () => {
    this.selectPdf(this.fileInput.files);
  };

  /**
   * apre il picture picker
   */
  private async openPicturePicker(): Promise<void> {
    const picture = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.Uri,
      source: CameraSource.Photos
    });

    await this.selectPicture(picture);
  }

  /**
   * apre il picker di mitek
   */
  private async openMitekPicker(): Promise<void> {
    this.mode = 'picture';

    let captureType: MitekDocType;

    if (this.format === 'card') {
      captureType = 'DL_FRONT';
    } else if (this.format === 'passport') {
      captureType = 'PASSPORT';
    } else {
      captureType = 'DOCUMENT';
    }

    this.mitekService
      .takeDocument(captureType)
      .pipe(takeWhile(update => update.status !== 'success', true))
      .subscribe(
        async update => {
          if (update.status === 'start') {
            this.uiService.showAppLoader();
            return;
          }

          if (update.status === 'acquiring') {
            this.uiService.hideAppLoader();
            return;
          }

          if (update.status === 'success') {
            const file = await this.createFileFromWebPath(
              update.result.imageData
            );

            this.selectFile(file, update.result.imageData);
          }
        },
        () => {
          this.uiService.hideAppLoader();
          this.selectFile(null, null);
        }
      );
  }

  /**
   * seleziona il file
   */
  private selectPdf(files: FileList): void {
    if (!files || !files[0]) {
      return this.selectFile(null, null);
    }

    const file = files[0];
    const src = URL.createObjectURL(file);

    this.selectFile(file, src);
  }

  /**
   * seleziona la foto
   */
  private async selectPicture(picture: Photo): Promise<void> {
    if (!picture) {
      return this.selectFile(null, null);
    }

    const file = await this.createFileFromWebPath(
      picture.webPath,
      picture.format
    );

    this.selectFile(file, picture.webPath);
  }

  /**
   * emette file change
   */
  private selectFile(file: File, src: string): void {
    const notAllowedFileType = !!file && !this.isFileTypeAllowed(file.type);

    if (notAllowedFileType) {
      this.showFileTypeWarning(file.type);
    }

    if (!file || !src || notAllowedFileType) {
      file = null;
      src = null;
    }

    this.fileChange.emit({ file, src });
  }

  /**
   * crea il file dal webpath
   */
  private async createFileFromWebPath(
    webPath: string,
    format: string = 'jpeg'
  ): Promise<File> {
    const res = await fetch(webPath);
    const blob = await res.blob();

    const fileName = `picture.${format}`;

    return new File([blob], fileName, { type: blob.type });
  }

  /**
   * controlla che il type del file sia abilitato
   */
  private isFileTypeAllowed(type: string): boolean {
    return (
      !!this.allowedTypes[this.mode] &&
      this.allowedTypes[this.mode].indexOf(type as FileType) > -1
    );
  }

  /**
   * crea l'input
   */
  private createFileElements(): void {
    if (!!this.fileForm && !!this.fileInput) {
      return;
    }

    this.fileForm = document.createElement('form');
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = '.pdf';
    this.fileInput.multiple = false;
    this.fileInput.addEventListener('change', this.fileInputChangeListener);
    this.fileForm.appendChild(this.fileInput);
  }

  /**
   * rimuove l'input
   */
  private removeFileElements(): void {
    if (!!this.fileInput) {
      this.fileInput.removeEventListener(
        'change',
        this.fileInputChangeListener
      );
      this.fileInput.remove();
    }

    if (!!this.fileForm) {
      this.fileForm.remove();
    }
  }

  /**
   * visualizza warning su file type non accettato
   */
  private showFileTypeWarning(type: string): void {
    this.uiService.showWarningToast(`L'estensione "${type}" non è consentita`);
  }
}
